import React, { useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import UniversalInvoicePrint from "./UniversalInvoicePrint"; 
import api from "../Service/api";
import "./Purchases.css";

function ViewSales() {

  const getLocalDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const formatToDateOnly = (datetime) => {
    if (!datetime) return '-';
    if (typeof datetime === 'string' && datetime.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return datetime;
    }
    try {
      const date = new Date(datetime);
      if (isNaN(date.getTime())) return '-';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '-';
    }
  };

  // Helper to sort invoices descending by invoice number
const sortInvoicesDesc = (arr) => {
  return arr.sort((a, b) => (parseInt(b.invoiceNumber) || 0) - (parseInt(a.invoiceNumber) || 0));
};
  
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updateInvoice, setUpdateInvoice] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [selectedCustomerBalance, setSelectedCustomerBalance] = useState(null);
  const [startDate, setStartDate] = useState(getLocalDate)
  const [endDate, setEndDate] = useState(getLocalDate)

  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  const printRef = useRef();
  const [printInvoice, setPrintInvoice] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const isInitialLoading = loadingProducts || loadingCategories || loadingVendors || loadingCustomers;

  useEffect(() => {
    setLoadingVendors(true);
    api.get("/product/getAll")
      .then(res => {
        if (res.data.code === 1 && Array.isArray(res.data.additionalDetail)) {
          setVendorList(res.data.additionalDetail);
        }
      })
      .catch(err => console.error("Failed to fetch vendors", err))
      .finally(() => setLoadingVendors(false));
  }, []);

  useEffect(() => {
    setLoadingCustomers(true);
    api.get("/product/getAllCustomers")
      .then(res => {
        if (res.data.code === 1 && Array.isArray(res.data.additionalDetail)) {
          setCustomerList(res.data.additionalDetail);
        }
      })
      .catch(err => console.error("Failed to fetch customers", err))
      .finally(() => setLoadingCustomers(false));
  }, []);

  const refreshProductList = async () => {
    setLoadingProducts(true);
    try {
      const res = await api.get("/product/names-prices");
      if (res.data.code === 1) {
        setAllProducts(res.data.additionalDetail);
      }
    } catch (err) {
      console.error("Error refreshing product list", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    const phone = localStorage.getItem("userPhone");
    if (!phone) return;
    api.get(`/authentication/userData/${phone}`)
      .then(res => setLoggedInUser(res.data.additionalDetail))
      .catch(err => console.error("USER DATA API ERROR:", err));
  }, []);
  
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: printInvoice ? `Invoice-${printInvoice.invoiceNumber}` : "Invoice",
  });
  
  const handlePrintInvoice = (invoice) => {
    const copy = JSON.parse(JSON.stringify(invoice));
    copy.user = loggedInUser;
    copy.products = (copy.sales || copy.products || []).map((p) => ({
      ...p,
      calculatedTotal: computeProductTotal(p),
    }));
    copy.payBill = invoice.payBill ?? invoice.amountPaid ?? 0;
    copy.grandTotal = computeInvoiceGrandTotal(copy);
    setPrintInvoice(copy);
    setTimeout(() => handlePrint(), 300);
  };

  useEffect(() => {
    loadSales();
    loadProductList();
    loadCategoryList();
  }, []);

  useEffect(() => {
    if (loading === false) {
      loadSales();
    }
  }, [startDate, endDate]);

  const loadSales = async () => {
    try {
      const params = { status: "Sale" };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await api.get("/product/sales/by-category-and-name", { params });
      const data = res.data.code === 1 ? res.data.additionalDetail || [] : [];
const sortedData = sortInvoicesDesc(data);   // ← add this line
setInvoices(sortedData);
setFilteredInvoices(sortedData);
    } catch (err) {
      console.error("Error fetching purchases", err);
      setInvoices([]);
      setFilteredInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setStartDate("");
    setEndDate("");
    setSearchText("");
    loadSales();
  };

  const handleSearch = (text) => {
    setSearchText(text);
    if (!text) {
      setFilteredInvoices(invoices);
      return;
    }
    const filtered = invoices.filter(inv => {
  const customerMatch = inv.customerName?.toLowerCase().includes(text.toLowerCase());
  const productMatch = (inv.sales || []).some(p => p.productName?.toLowerCase().includes(text.toLowerCase()));
  const invoiceMatch = inv.invoiceNumber?.toString().includes(text);
  return customerMatch || productMatch || invoiceMatch;
});
setFilteredInvoices(sortInvoicesDesc(filtered));   // ← add sort here
  };

  const loadProductList = async () => {
    setLoadingProducts(true);
    try {
      const res = await api.get("/product/names-prices");
      if (res.data.code === 1) {
        setAllProducts(res.data.additionalDetail);
      }
    } catch (err) {
      console.error("Error loading product list", err);
      setAllProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadCategoryList = async () => {
    setLoadingCategories(true);
    try {
      const res = await api.get("/api/category/getCategories");
      setCategoryList(res.data.additionalDetail || []);
    } catch (err) {
      setCategoryList([
        { categoryName: "packet" }, { categoryName: "roll" }, { categoryName: "reel" },
        { categoryName: "tota" }, { categoryName: "box" }, { categoryName: "kg" }, { categoryName: "bottle" },
      ]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const computeProductTotal = (prod) => {
    const category = (prod.category || "").toLowerCase();
    const price = parseFloat(prod.price) || 0;
    const quantity = parseFloat(prod.quantity) || 0;
    const size = parseFloat(prod.size) || 1;
    const ktae = parseFloat(prod.ktae) || 1;
    const gram = parseFloat(prod.gram) || 1;
    let total = 0;
    if (category === "packet") total = (quantity * size * ktae * gram * price) / 15500;
    else if (category === "roll") total = size * quantity * price;
    else if (category === "reel" || category === "tota") total = quantity * price;
    else if (category === "box") total = price * quantity;
    else total = price * quantity;
    if (prod.generalDiscount) total -= parseFloat(prod.generalDiscount) || 0;
    return Number(total.toFixed(2));
  };

  const openUpdateForm = (invoice) => {
    const copy = JSON.parse(JSON.stringify(invoice || {}));
    copy.sales = (copy.sales || []).map((p) => ({
      ...p,
      quantity: p.quantity ?? 0,
      price: p.price ?? 0,
      size: p.size ?? "",
      length: p.length ?? "",
      ktae: p.ktae ?? "",
      gram: p.gram ?? "",
      boxSize: p.boxSize ?? "",
      particular: p.particular ?? "",
      payBill: p.payBill ?? "",
      generalDiscount: p.generalDiscount ?? "",
      calculatedTotal: computeProductTotal(p),
    }));
    copy.customerName = invoice.customerName ?? "";
    copy.invoiceDiscount = invoice.invoiceDiscount ?? 0;
    copy.invoiceRent = invoice.invoiceRent ?? 0;
    copy.payBill = invoice.payBill ?? invoice.amountPaid ?? "";
    copy.description = invoice.description ?? "";
    copy.gstPercentage = invoice.gstPercentage ?? 0;
    copy.gstAmount = invoice.gstAmount ?? 0;
    copy.totalBeforeGst = invoice.totalBeforeGst ?? 0;
    copy.invoiceDate = invoice.invoiceDate || getLocalDate();
    copy.grandTotal = computeInvoiceGrandTotal(copy);
    
    // ✅ Add driver fields to update form
    copy.driverName = invoice.driverName ?? "";
    copy.vehicleNumber = invoice.vehicleNumber ?? "";
    
    setSelectedCustomerBalance(null);
    setUpdateInvoice(copy);
  };

  const handleUpdateCustomerChange = (e) => {
    const name = e.target.value;
    setUpdateInvoice(prev => ({ ...prev, customerName: name }));
    const customer = customerList.find(c => c.customerName === name);
    if (customer && customer.currentBalance !== undefined) {
      setSelectedCustomerBalance(customer.currentBalance);
    } else {
      setSelectedCustomerBalance(null);
    }
  };

  // ✅ Handle driver field changes in update form
  const handleDriverChange = (field, value) => {
    if (!updateInvoice) return;
    setUpdateInvoice(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (updateInvoice) {
      const recalculated = computeInvoiceGrandTotal(updateInvoice);
      setUpdateInvoice(prev => ({ ...prev, grandTotal: recalculated }));
    }
  }, [updateInvoice?.gstPercentage, updateInvoice?.invoiceDiscount, 
      updateInvoice?.invoiceRent, updateInvoice?.sales, updateInvoice?.payBill]);

  const computeInvoiceGrandTotal = (invoice) => {
    if (!invoice) return 0;
    const productsTotal = (invoice.sales || []).reduce((total, p) => {
      const category = (p.category || "").toLowerCase();
      const price = parseFloat(p.price) || 0;
      const quantity = parseFloat(p.quantity) || 0;
      const size = parseFloat(p.size) || 1;
      const ktae = parseFloat(p.ktae) || 1;
      const gram = parseFloat(p.gram) || 1;
      let productTotal = 0;
      if (category === "packet") productTotal = (quantity * price * size * ktae * gram) / 15500;
      else if (category === "roll") productTotal = quantity * price * size;
      else if (category === "reel" || category === "tota") productTotal = quantity * price;
      else if (category === "box") productTotal = quantity * price;
      else productTotal = quantity * price;
      if (p.generalDiscount) productTotal -= parseFloat(p.generalDiscount) || 0;
      return total + productTotal;
    }, 0);
    let subtotalBeforeGst = productsTotal;
    const discount = parseFloat(invoice.invoiceDiscount) || 0;
    const rent = parseFloat(invoice.invoiceRent) || 0;
    subtotalBeforeGst = subtotalBeforeGst - discount + rent;
    invoice.totalBeforeGst = subtotalBeforeGst;
    let gstAmount = 0;
    const gstPercentage = parseFloat(invoice.gstPercentage) || 0;
    if (gstPercentage > 0) {
      gstAmount = (subtotalBeforeGst * gstPercentage / 100);
      invoice.gstAmount = gstAmount;
    } else {
      invoice.gstAmount = 0;
    }
    let grandTotal = subtotalBeforeGst + gstAmount;
    const payBill = parseFloat(invoice.payBill) || 0;
    grandTotal -= payBill;
    return Number(grandTotal.toFixed(2));
  };

  useEffect(() => {
  const handleWheel = (e) => {
    if (e.target.type === 'number') {
      e.preventDefault();
    }
  };
  document.addEventListener('wheel', handleWheel, { passive: false });
  return () => document.removeEventListener('wheel', handleWheel);
}, []);

  const handleUpdateProductChange = (index, field, value) => {
    if (!updateInvoice) return;
    const products = [...updateInvoice.sales];
    const prod = { ...products[index] };
    prod[field] = value;

    if (field === "productName") {
      const match = allProducts.find(
        (ap) => (ap.productName || "").toLowerCase() === value.toLowerCase()
      );
      if (match) {
        prod.price = match.sellPrice ?? prod.price;
        prod.category = match.category ?? prod.category;
        if (match.category && match.category.toLowerCase() === 'roll' && match.size) {
          prod.size = match.size;
        }
      }
    }

    const currentCategory = (prod.category || "").toLowerCase();
    if (field === "quantity" && (currentCategory === "reel" || currentCategory === "tota" || currentCategory === "roll")) {
      const weightValue = parseFloat(value) || 0;
      prod.size = weightValue;
    }

    if (field === "category") {
      const cat = (value || "").toLowerCase();
      if (cat !== 'box') prod.boxSize = '';
      if (cat !== 'packet') { prod.ktae = ''; prod.gram = ''; }
      if (!['packet', 'roll', 'reel', 'tota', 'box'].includes(cat)) prod.size = '';
      if (cat !== 'reel' && cat !== 'tota') prod.length = '';
    }

    prod.calculatedTotal = computeProductTotal(prod);
    products[index] = prod;
    const updatedInvoice = { ...updateInvoice, sales: products };
    updatedInvoice.grandTotal = computeInvoiceGrandTotal(updatedInvoice);
    setUpdateInvoice(updatedInvoice);
  };

  const handleUpdateInvoiceField = (field, value) => {
    if (!updateInvoice) return;
    const updated = { ...updateInvoice, [field]: value };
    updated.grandTotal = computeInvoiceGrandTotal(updated);
    setUpdateInvoice(updated);
  };

  const saveUpdate = async () => {
    if (!updateInvoice) return alert("No invoice to update");
    setIsUpdating(true);
    try {
      const toSend = JSON.parse(JSON.stringify(updateInvoice));
      toSend.products = (toSend.sales || []).map((p) => {
        const cat = (p.category || "").toLowerCase();
        const clean = { ...p };
        if (cat === 'box') {
          clean.boxSize = p.boxSize || "";
          clean.particular = p.particular || "";
          clean.size = 0; clean.ktae = 0; clean.gram = 0;
        } else if (cat === 'packet') {
          clean.size = parseFloat(p.size) || 0;
          clean.ktae = parseFloat(p.ktae) || 0;
          clean.gram = parseFloat(p.gram) || 0;
          clean.boxSize = null;
          clean.particular = p.particular || "";
        } else if (cat === 'roll') {
          clean.size = parseFloat(p.size) || 0;
          clean.ktae = 0; clean.gram = 0;
          clean.boxSize = null;
          clean.particular = p.particular || "";
        } else if (cat === 'reel' || cat === 'tota') {
          clean.size = parseFloat(p.quantity) || 0;
          clean.length = parseFloat(p.length) || 0;
          clean.ktae = 0; clean.gram = 0;
          clean.boxSize = null;
          clean.particular = p.particular || "";
        } else {
          clean.size = 0; clean.ktae = 0; clean.gram = 0;
          clean.boxSize = null;
          clean.particular = p.particular || "";
        }
        clean.calculatedTotal = computeProductTotal(clean);
        return clean;
      });
      toSend.gstPercentage = updateInvoice.gstPercentage || null;
      toSend.gstAmount = updateInvoice.gstAmount || null;
      toSend.totalBeforeGst = updateInvoice.totalBeforeGst || null;
      
      // ✅ Add driver fields to the update payload
      toSend.driverName = updateInvoice.driverName || "";
      toSend.vehicleNumber = updateInvoice.vehicleNumber || "";
      
      const invoiceNum = toSend.invoiceNumber;
      await api.put(`/product/saleUpdated/${invoiceNum}`, toSend, {
        params: { status: "Sale", invoiceDate: toSend.invoiceDate }
      });
      alert("Invoice updated successfully!");
      setUpdateInvoice(null);
      setSelectedCustomerBalance(null);
      await refreshProductList();
      loadSales();
    } catch (err) {
      console.error("Update failed", err);
      alert("Update failed! See console.");
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteInvoice = async (invoiceNum) => {
    if (!window.confirm(`Delete invoice ${invoiceNum}?`)) return;
    try {
      await api.delete(`/product/delete/sale/${invoiceNum}`, {params: {status: "Sale"}});
      alert("Deleted!");
      loadSales();
    } catch (err) {
      console.error("Delete failed", err);
      alert("Delete failed");
    }
  };

  const showWeightLabel = updateInvoice?.sales?.some(p => {
    const cat = (p.category || "").toLowerCase();
    return cat === "reel" || cat === "tota" || cat === "roll";
  });

  if (loading) return <div className="loading-text">Loading…</div>;

  const invoicesWithMissingRates = filteredInvoices.filter(inv =>
  inv.isActive !== false &&   // ⬅️ skip canceled invoices
  (inv.sales || []).some(p => !p.price || parseFloat(p.price) === 0)
);

const missingRateInvoiceNumbers = invoicesWithMissingRates.map(inv => inv.invoiceNumber).join(', ');

  return (
    <div className="purchase-container">
      <h2 className="page-title">📄 View Sales</h2>

      {isInitialLoading && (
        <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#e3f2fd', marginBottom: '10px', borderRadius: '4px' }}>
          ⏳ Loading data... {loadingProducts && "Products "} {loadingCategories && "Categories "} {loadingVendors && "Vendors "} {loadingCustomers && "Customers "}
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          placeholder="Search by product, customer, or invoice number..."
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          className="read-only-input"
          style={{ width: 300, padding: 6 }}
          disabled={isInitialLoading}
        />
      </div>

      <div style={{ marginBottom: 20, display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontWeight: '500', color: '#000', fontSize: '14px' }}>From:</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="read-only-input" style={{ padding: '6px', borderRadius: '4px' }} disabled={isInitialLoading} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontWeight: '500', color: '#000', fontSize: '14px' }}>To:</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="read-only-input" style={{ padding: '6px', borderRadius: '4px' }} min={startDate} disabled={isInitialLoading} />
        </div>
        <button onClick={resetFilters} className="remove-btn" style={{ padding: '6px 12px', fontSize: '14px', marginLeft: '10px' }} disabled={isInitialLoading}>Clear Filters</button>
        {(startDate || endDate || searchText) && (
          <span style={{ fontSize: '14px', color: '#555', marginLeft: '10px' }}>
            Showing {filteredInvoices.length} of {invoices.length} sales
            {searchText && ` for "${searchText}"`}
            {startDate && ` from ${startDate}`}
            {endDate && ` to ${endDate}`}
          </span>
        )}
      </div>

{/* 2. Render the banner */}
{invoicesWithMissingRates.length > 0 && (
  <div style={{
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '10px 15px',
    borderRadius: '4px',
    marginBottom: '15px',
    border: '1px solid #ffeeba',
    fontWeight: '500'
  }}>
    ⚠️ Warning: The following invoice(s) have missing product rates: <strong>{missingRateInvoiceNumbers}</strong>. Please update them.
  </div>
)}

      {filteredInvoices.length === 0 && !loading && (
        <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          {searchText || startDate || endDate ? "No sales found matching your search/filters." : "No sales found."}
        </p>
      )}

      {filteredInvoices.map((invoice) => {
        const products = invoice.sales || [];
        const categories = products.map(p => (p.category || "").toLowerCase());
        const showPacketColumns = categories.includes("packet");
        const showRollColumns = categories.some(c => ["roll"].includes(c));
        const showBoxColumns = categories.includes("box");
        const showParticularColumn = true;
        const showLengthColumn = categories.some(c => c === "reel" || c === "tota");
        const showSizeColumn = showPacketColumns || showRollColumns || showLengthColumn;

        const hasMissingRate = (invoice.sales || []).some(
  (p) => !p.price || parseFloat(p.price) === 0
);
  const isCanceled = invoice.isActive === false;

        return (
          <div key={invoice.invoiceNumber} className="invoice-box">

{/* 🔴 Canceled banner */}
      {isCanceled && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '12px 16px',
          borderRadius: '4px',
          marginBottom: '15px',
          border: '2px solid #f5c6cb',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontSize: '24px' }}>⛔</span>
          <span>THIS INVOICE HAS BEEN CANCELED / DELETED – Read‑only mode</span>
        </div>
      )}
            {hasMissingRate && !isCanceled && (
        <div style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '8px 12px',
          borderRadius: '4px',
          marginBottom: '10px',
          border: '1px solid #ffeeba',
          fontWeight: '500'
        }}>
          ⚠️ Warning: This invoice contains product(s) with missing rate (price). Please update.
        </div>
      )}

      {/* Invoice title with Canceled badge */}
      <h3 className="invoice-title" style={{ color: "#000" }}>
        Invoice #{invoice.invoiceNumber} – {invoice.customerName}
        {isCanceled && (
          <span style={{
            marginLeft: '15px',
            backgroundColor: '#dc3545',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            CANCELLED
          </span>
        )}
      </h3>
            {/* ✅ Display Driver Name and Vehicle Number */}
            {(invoice.driverName || invoice.vehicleNumber) && (
              <div style={{ 
                marginBottom: "10px", 
                padding: "8px 12px", 
                backgroundColor: "#e3f2fd", 
                borderRadius: "4px",
                border: "1px solid #90caf9",
                display: "flex",
                gap: "20px",
                flexWrap: "wrap"
              }}>
                {invoice.driverName && (
                  <span><strong>👨‍💼 Driver:</strong> {invoice.driverName}</span>
                )}
                {invoice.vehicleNumber && (
                  <span><strong>🚛 Vehicle:</strong> {invoice.vehicleNumber}</span>
                )}
              </div>
            )}

            {invoice.invoiceImagePath && (
              <div style={{ marginBottom: "10px", cursor: "pointer" }}>
                <img src={invoice.invoiceImagePath} alt={`Invoice ${invoice.invoiceNumber}`} style={{ maxWidth: "200px", maxHeight: "200px", border: "1px solid #ccc", padding: "5px", objectFit: "contain" }} onClick={() => setFullScreenImage(invoice.invoiceImagePath)} />
              </div>
            )}

{!isCanceled && (
        <div style={{ marginBottom: "10px", display: "flex", gap: "10px" }}>
          <button className="save-btn" onClick={() => openUpdateForm(invoice)} disabled={isInitialLoading}>Update</button>
          <button className="remove-btn" onClick={() => deleteInvoice(invoice.invoiceNumber)} disabled={isInitialLoading}>Delete</button>
          <button className="print-btn" onClick={() => handlePrintInvoice(invoice)} disabled={!invoice || isInitialLoading}>Print</button>
        </div>
      )}

      {/* If canceled, show a message instead of buttons */}
      {isCanceled && (
        <div style={{ marginBottom: "10px", color: "#6c757d", fontStyle: "italic" }}>
          🔒 This invoice is canceled and cannot be modified.
        </div>
      )}

            <div className="purchase-table-container">
              <div className="table-scroll-wrapper">
                <table className="purchase-table">
                  <thead>
                    <tr>
                      <th>Product</th><th>Category</th><th>Quantity</th><th>Rate</th>
                      <th>Sale Date</th><th>Updated Date</th>
                      {showSizeColumn && <th>Size</th>}
                      {showPacketColumns && (<><th>Ktae</th><th>Gram</th></>)}
                      {showBoxColumns && <th>Box Size</th>}
                      {showParticularColumn && <th>Vendor/Particular</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, idx) => {
                      const category = (p.category || "").toLowerCase();
                      const shouldShowParticular = category === "reel" || category === "tota";
                      const isRoll = category === "roll";
                      const isPacket = category === "packet";
                      const isReelOrTota = category === "reel" || category === "tota";
                      
                      let sizeDisplay = '-';
                      if (isPacket || isRoll) {
                        sizeDisplay = p.size || '-';
                      } else if (isReelOrTota) {
                        sizeDisplay = p.length || '-';
                      }
                      
                      return (
                        <tr key={idx}>
                          <td>{p.productName}</td>
                          <td>{p.category}</td>
                          <td>{p.quantity}</td>
                          <td>{p.price}</td>
                          <td>{formatToDateOnly(p.saleEntryTime)}</td>
                          <td>{formatToDateOnly(p.recordUpdatedTime)}</td>
                          {showSizeColumn && <td>{sizeDisplay}</td>}
                          {showPacketColumns && (
                            <>
                              <td>{isPacket ? (p.ktae || '-') : '-'}</td>
                              <td>{isPacket ? (p.gram || '-') : '-'}</td>
                            </>
                          )}
                          {showBoxColumns && (
                            <td>{category === 'box' ? (p.boxSize || '-') : '-'}</td>
                          )}
                          {showParticularColumn && (
                            <td>
                              {category !== 'roll' ? (
                                shouldShowParticular ? (p.particular || '-') : (p.particular || '-')
                              ) : (
                                '-'
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* UPDATE FORM */}
            {updateInvoice && updateInvoice.invoiceNumber === invoice.invoiceNumber && (
              <div className="invoice-box" style={{ background: "#fff7e6", marginTop: 12 }}>
                <h3 className="invoice-title">Update Invoice #{updateInvoice.invoiceNumber}</h3>

                <div style={{ marginBottom: 15, padding: "0 10px", display: "flex", gap: "20px", flexWrap: "wrap" }}>
                  <div>
                    <label style={{ fontWeight: "bold", marginRight: 10 }}>Invoice Date:</label>
                    <input type="date" value={updateInvoice.invoiceDate || getLocalDate()} onChange={(e) => handleUpdateInvoiceField("invoiceDate", e.target.value)} className="read-only-input" style={{ width: "200px" }} max={new Date().toISOString().split('T')[0]} disabled={isUpdating} />
                  </div>
                  
                  {/* ✅ Driver Name and Vehicle Number in Update Form */}
                  <div>
                    <label style={{ fontWeight: "bold", marginRight: 10 }}>Driver Name:</label>
                    <input type="text" value={updateInvoice.driverName || ""} onChange={(e) => handleDriverChange("driverName", e.target.value)} className="read-only-input" style={{ width: "180px" }} placeholder="Enter driver name" disabled={isUpdating} />
                  </div>
                  
                  <div>
                    <label style={{ fontWeight: "bold", marginRight: 10 }}>Vehicle Number:</label>
                    <input type="text" value={updateInvoice.vehicleNumber || ""} onChange={(e) => handleDriverChange("vehicleNumber", e.target.value)} className="read-only-input" style={{ width: "160px" }} placeholder="Enter vehicle number" disabled={isUpdating} />
                  </div>
                </div>

                <div className="purchase-table-container">
                  <div className="table-scroll-wrapper">
                    <table className="purchase-table">
                      <thead>
                        <tr>
                          <th>Product Name</th><th>Category</th>
                          <th>{showWeightLabel ? "Weight/Quantity" : "Quantity"}</th>
                          <th>Rate</th>
                          {(() => {
                            const updateCategories = updateInvoice.sales.map(p => (p.category || "").toLowerCase());
                            const showPacket = updateCategories.includes("packet");
                            const showRoll = updateCategories.some(c => ["roll"].includes(c));
                            const showBox = updateCategories.includes("box");
                            const showParticular = true;
                            const showLength = updateCategories.some(c => c === "reel" || c === "tota");
                            const showSize = showPacket || showRoll || showLength;
                            return (
                              <>
                                {showSize && <th>Size</th>}
                                {showPacket && (<><th>Ktae</th><th>Gram</th></>)}
                                {showBox && <th>Box Size</th>}
                                {showParticular && <th>Vendor/Particular</th>}
                              </>
                            );
                          })()}
                        </tr>
                      </thead>
                      <tbody>
                        {updateInvoice.sales.map((p, idx) => {
                          const cat = (p.category || "").toLowerCase();
                          const updateCategories = updateInvoice.sales.map(p => (p.category || "").toLowerCase());
                          const showPacket = updateCategories.includes("packet");
                          const showRoll = updateCategories.some(c => ["roll"].includes(c));
                          const showBox = updateCategories.includes("box");
                          const showParticular = true;
                          const showLength = updateCategories.some(c => c === "reel" || c === "tota");
                          const showSize = showPacket || showRoll || showLength;
                          const isReelOrTota = cat === "reel" || cat === "tota";
                          const isRoll = cat === "roll";
                          const isPacket = cat === "packet";
                          const isBox = cat === "box";
                          
                          const selectedProduct = allProducts.find(ap => ap.productName === p.productName);
                          const stockQuantity = selectedProduct?.stockQuantity;
                          const stockByWeight = selectedProduct?.stockByWeight;
                          const packetStockByVariant = selectedProduct?.packetStockByVariant;
                          
                          return (
                            <tr key={idx}>
                              <td style={{ position: 'relative' }}>
                                <input list={`products-${idx}`} value={p.productName || ""} onChange={(e) => handleUpdateProductChange(idx, "productName", e.target.value)} className="read-only-input" disabled={isUpdating} />
                                <datalist id={`products-${idx}`}>
                                  {allProducts.map((ap, i) => {
                                    const apCat = (ap.category || "").toLowerCase();
                                    const isReelTotaRoll = apCat === 'reel' || apCat === 'tota' || apCat === 'roll';
                                    const isPacket = apCat === 'packet';
                                    if (isReelTotaRoll && ap.stockByWeight && Object.keys(ap.stockByWeight).length > 0) {
                                      const nonZero = Object.entries(ap.stockByWeight).filter(([_, qty]) => qty > 0);
                                      if (nonZero.length > 0) {
                                        return <option key={i} value={ap.productName}>{ap.productName} [{nonZero.map(([w, q]) => `${w}: ${q}`).join(', ')}]</option>;
                                      } else {
                                        return <option key={i} value={ap.productName}>{ap.productName} (Out of Stock)</option>;
                                      }
                                    } else if (isPacket && ap.packetStockByVariant && Object.keys(ap.packetStockByVariant).length > 0) {
                                      const nonZero = Object.entries(ap.packetStockByVariant).filter(([_, qty]) => qty > 0);
                                      if (nonZero.length > 0) {
                                        return <option key={i} value={ap.productName}>{ap.productName} [{nonZero.map(([v, q]) => `${v}: ${q}`).join(', ')}]</option>;
                                      } else {
                                        return <option key={i} value={ap.productName}>{ap.productName} (Out of Stock)</option>;
                                      }
                                    } else {
                                      return <option key={i} value={ap.productName}>{ap.productName} {ap.stockQuantity !== undefined ? `(Stock: ${ap.stockQuantity})` : ''}{ap.category === 'roll' && ap.size ? ` [Size: ${ap.size}]` : ''}</option>;
                                    }
                                  })}
                                </datalist>
                                
                                {isPacket && selectedProduct && !isUpdating && (
                                  <div style={{ marginTop: '4px' }}>
                                    {(() => {
                                      const totalStock = selectedProduct.stockQuantity || 0;
                                      const variantEntries = packetStockByVariant ? Object.entries(packetStockByVariant).filter(([_, qty]) => qty > 0) : [];
                                      const hasStock = totalStock > 0;
                                      if (!hasStock) {
                                        return <div style={{ color: '#ff4444', backgroundColor: '#ffebee', padding: '6px 8px', borderRadius: '4px', border: '1px solid #ff4444', fontSize: '11px' }}><strong>⚠️ Out of Stock</strong></div>;
                                      }
                                      return (
                                        <div style={{ color: '#0a8938', backgroundColor: '#f8f9fa', padding: '6px 8px', borderRadius: '4px', border: '1px solid #0a8938', fontSize: '11px' }}>
                                          <div><strong>📦 Total Stock: {totalStock}</strong></div>
                                          {variantEntries.length > 0 && (
                                            <>
                                              <div style={{ fontSize: '10px', marginTop: '4px', color: '#555' }}>Breakdown by variant:</div>
                                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '2px' }}>
                                                {variantEntries.map(([variant, qty]) => (
                                                  <span key={variant} style={{ backgroundColor: '#e8f5e9', padding: '2px 6px', borderRadius: '12px', fontSize: '9px', fontWeight: 'bold' }}>{variant}: {qty}</span>
                                                ))}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}
                                
                                {(isReelOrTota || isRoll) && stockByWeight && Object.keys(stockByWeight).length > 0 && !isUpdating && (
                                  <div style={{ fontSize: '10px', color: '#0a8938', marginTop: '2px', fontWeight: '500', backgroundColor: '#e8f5e9', padding: '2px 4px', borderRadius: '4px' }}>
                                    📊 {Object.entries(stockByWeight).map(([w, q]) => `${w}: ${q}`).join(', ')}
                                  </div>
                                )}
                                
                                {!isPacket && !isReelOrTota && !isRoll && stockQuantity !== undefined && p.productName && !isUpdating && (
                                  <div style={{ fontSize: '11px', color: parseFloat(stockQuantity) < 0 ? '#ff4444' : '#0a8938', marginTop: '2px', fontWeight: '500' }}>
                                    📦 Current Stock: {stockQuantity}
                                  </div>
                                )}
                                
                                {isRoll && !p.size && p.productName && !isUpdating && (
                                  <div style={{ fontSize: '11px', color: '#ff4444', marginTop: '2px', fontWeight: '500' }}>⚠️ Size is required for roll products</div>
                                )}
                                {(isReelOrTota || isRoll) && (!p.quantity || parseFloat(p.quantity) <= 0) && p.productName && !isUpdating && (
                                  <div style={{ fontSize: '11px', color: '#ff9800', marginTop: '2px', fontWeight: '500' }}>
                                    ⚡ Enter {isRoll ? 'meters/units' : 'weight in kg'} in the Quantity column
                                  </div>
                                )}
                                {isReelOrTota && (!p.length || parseFloat(p.length) <= 0) && p.productName && !isUpdating && (
                                  <div style={{ fontSize: '11px', color: '#ff9800', marginTop: '2px', fontWeight: '500' }}>
                                    ⚡ Length is required (e.g., meters)
                                  </div>
                                )}
                              </td>

                              <td>
                                <input list={`categories-${idx}`} value={p.category || ""} onChange={(e) => handleUpdateProductChange(idx, "category", e.target.value)} className="read-only-input" disabled={isUpdating} />
                                <datalist id={`categories-${idx}`}>
                                  {Array.isArray(categoryList) && categoryList.map((c, i) => <option key={i} value={c.categoryName} />)}
                                </datalist>
                              </td>

                              <td>
                                <input type="number" value={p.quantity ?? ""} onChange={(e) => handleUpdateProductChange(idx, "quantity", e.target.value)} className="read-only-input" disabled={isUpdating} placeholder={(isReelOrTota) ? "Weight in kg" : (isRoll ? "Quantity in meters" : "Quantity")} step={(isReelOrTota) ? "1" : "0.01"}   onWheel={(e) => e.preventDefault()}   // ✅ add this
/>
                              </td>

                              <td>
                                <input type="number" value={p.price ?? ""} onChange={(e) => handleUpdateProductChange(idx, "price", e.target.value)} className="read-only-input" disabled={isUpdating}   onWheel={(e) => e.preventDefault()}   // ✅ add this
/>
                              </td>

                              {showSize && (
                                <td>
                                  {(isPacket || isRoll) ? (
                                    <input type="number" value={p.size ?? ""} onChange={(e) => handleUpdateProductChange(idx, "size", e.target.value)} className="read-only-input" placeholder="Size" disabled={isUpdating} style={{ borderColor: (isRoll && !p.size && p.productName) ? '#ff4444' : '' }}   onWheel={(e) => e.preventDefault()}   // ✅ add this
/>
                                  ) : isReelOrTota ? (
                                    <input type="number" step="any" value={p.length ?? ""} onChange={(e) => handleUpdateProductChange(idx, "length", e.target.value)} className="read-only-input" placeholder="Length (m)" disabled={isUpdating} style={{ borderColor: (!p.length && p.productName) ? '#ff4444' : '' }}   onWheel={(e) => e.preventDefault()}   // ✅ add this
/>
                                  ) : (
                                    <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />
                                  )}
                                </td>
                              )}

                              {showPacket && (
                                <>
                                  <td>
                                    {isPacket ? <input type="number" value={p.ktae ?? ""} onChange={(e) => handleUpdateProductChange(idx, "ktae", e.target.value)} className="read-only-input" placeholder="Ktae" disabled={isUpdating}   onWheel={(e) => e.preventDefault()}   // ✅ add this
 /> : <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A"
/>}
                                  </td>
                                  <td>
                                    {isPacket ? <input type="number" value={p.gram ?? ""} onChange={(e) => handleUpdateProductChange(idx, "gram", e.target.value)} className="read-only-input" placeholder="Gram" disabled={isUpdating}   onWheel={(e) => e.preventDefault()}   // ✅ add this
/> : <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" 
/>}
                                  </td>
                                </>
                              )}

                              {showBox && (
                                <>
                                  <td>
                                    {isBox ? <input type="text" value={p.boxSize ?? ""} onChange={(e) => handleUpdateProductChange(idx, "boxSize", e.target.value)} className="read-only-input" placeholder="Box Size" disabled={isUpdating} /> : <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />}
                                  </td>
                                  <td>
                                    {isBox ? <input type="text" value={p.particular ?? ""} onChange={(e) => handleUpdateProductChange(idx, "particular", e.target.value)} className="read-only-input" placeholder="Particular" disabled={isUpdating} /> : <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="N/A" />}
                                  </td>
                                </>
                              )}

                              {showParticular && !isBox && (
  <td>
    {cat !== 'roll' ? (  // ✅ FIXED: Changed 'category' to 'cat'
      (isReelOrTota) ? (
        <>
          <input type="text" list={`vendors-${idx}`} value={p.particular ?? ""} onChange={(e) => handleUpdateProductChange(idx, "particular", e.target.value)} className="read-only-input" placeholder="Select Vendor Name" disabled={isUpdating} style={{ borderColor: (!p.particular && p.productName) ? '#ff4444' : '' }} />
          <datalist id={`vendors-${idx}`}>
            {vendorList.map(vendor => <option key={vendor.id} value={vendor.vendorName} />)}
          </datalist>
          {!p.particular && p.productName && !isUpdating && <div style={{ fontSize: '11px', color: '#ff4444', marginTop: '2px' }}>⚠️ Vendor name required</div>}
          {p.particular && !isUpdating && <div style={{ fontSize: '10px', color: '#0a8938', marginTop: '2px' }}>✅ Selling from vendor: {p.particular}</div>}
        </>
      ) : (
        <input type="text" value={p.particular ?? ""} onChange={(e) => handleUpdateProductChange(idx, "particular", e.target.value)} className="read-only-input" placeholder="Particular" disabled={isUpdating} />
      )
    ) : (
      <input type="text" value="" className="read-only-input" disabled style={{ backgroundColor: '#f0f0f0' }} placeholder="Not applicable for Roll" />
    )}
  </td>
)}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="invoice-summary" style={{ color: "#000", marginTop: 10 }}>
                  <p><strong>Customer Name:</strong>
                    <input type="text" list="customerNamesUpdate" value={updateInvoice.customerName ?? ""} onChange={handleUpdateCustomerChange} className="read-only-input" placeholder="Select Customer" disabled={isUpdating} />
                    <datalist id="customerNamesUpdate">
                      {customerList.map(c => <option key={c.id} value={c.customerName}>{c.customerName} {c.currentBalance !== undefined ? `(Balance: ${c.currentBalance})` : ''}</option>)}
                    </datalist>
                    {selectedCustomerBalance !== null && <span style={{ marginLeft: '10px', fontSize: '12px', color: '#0a8938' }}>Current Balance: Rs {selectedCustomerBalance.toFixed(2)}</span>}
                  </p>
                  <p><strong>Discount:</strong> <input type="number" value={updateInvoice.invoiceDiscount ?? ""} onChange={(e) => handleUpdateInvoiceField("invoiceDiscount", e.target.value)} className="read-only-input" disabled={isUpdating}   onWheel={(e) => e.preventDefault()}   // ✅ add this
/></p>
                  <p><strong>Rent:</strong> <input type="number" value={updateInvoice.invoiceRent ?? ""} onChange={(e) => handleUpdateInvoiceField("invoiceRent", e.target.value)} className="read-only-input" disabled={isUpdating}   onWheel={(e) => e.preventDefault()}   // ✅ add this
/></p>
                  <p><strong>GST Percentage:</strong> <input type="number" value={updateInvoice.gstPercentage ?? ""} onChange={(e) => handleUpdateInvoiceField("gstPercentage", e.target.value)} className="read-only-input" step="0.01" placeholder="e.g., 18" disabled={isUpdating}   onWheel={(e) => e.preventDefault()}   // ✅ add this
/></p>
                  {updateInvoice.gstPercentage > 0 && (
                    <p><strong>GST Amount:</strong> {(() => {
                      const productsTotal = (updateInvoice.sales || []).reduce((sum, p) => sum + (parseFloat(p.calculatedTotal) || 0), 0);
                      const discount = parseFloat(updateInvoice.invoiceDiscount) || 0;
                      const rent = parseFloat(updateInvoice.invoiceRent) || 0;
                      const gstPercent = parseFloat(updateInvoice.gstPercentage) || 0;
                      const subtotalBeforeGst = productsTotal - discount + rent;
                      const gstAmount = (subtotalBeforeGst * gstPercent / 100).toFixed(2);
                      return gstAmount;
                    })()}</p>
                  )}
                  <p><strong>Amount Paid:</strong> <input type="number" value={updateInvoice.payBill ?? ""} onChange={(e) => handleUpdateInvoiceField("payBill", e.target.value)} className="read-only-input" disabled={isUpdating}   onWheel={(e) => e.preventDefault()}   // ✅ add this
/></p>
                  <p><strong>Description:</strong> <input type="text" value={updateInvoice.description ?? ""} onChange={(e) => handleUpdateInvoiceField("description", e.target.value)} className="read-only-input" style={{ width: "60%" }} disabled={isUpdating} /></p>
                  <p className="grand-total"><strong>Amount Receivable:</strong> {updateInvoice.grandTotal ?? "0"}</p>

                  {isCanceled && (
          <p style={{ color: '#dc3545', fontWeight: 'bold' }}>
            <strong>Status:</strong> CANCELED
          </p>
        )}
                </div>

                <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
                  <button className="save-btn" onClick={saveUpdate} disabled={isUpdating} style={{ opacity: isUpdating ? 0.6 : 1, cursor: isUpdating ? 'not-allowed' : 'pointer' }}>{isUpdating ? 'Saving...' : 'Save Updated Invoice'}</button>
                  <button className="remove-btn" onClick={() => setUpdateInvoice(null)} disabled={isUpdating}>Cancel</button>
                </div>
              </div>
            )}

            <div className="invoice-summary" style={{ color: "#000", marginTop: 10 }}>
              <p><strong>Discount:</strong> {invoice.invoiceDiscount ?? "0"}</p>
              <p><strong>Rent:</strong> {invoice.invoiceRent ?? "0"}</p>
              {invoice.gstPercentage > 0 && (
                <>
                  <p><strong>GST ({invoice.gstPercentage}%):</strong> {invoice.gstAmount ?? "0"}</p>
                  <p><strong>Subtotal before GST:</strong> {invoice.totalBeforeGst ?? "0"}</p>
                </>
              )}
              <p><strong>Amount Paid:</strong> {invoice.amountPaid ?? "0"}</p>
              <p><strong>Description:</strong> {invoice.description ?? "—"}</p>
              
              {/* ✅ Display Driver and Vehicle in summary */}
              {(invoice.driverName || invoice.vehicleNumber) && (
                <div style={{ 
                  marginTop: "8px", 
                  padding: "6px 10px", 
                  backgroundColor: "#e8f5e9", 
                  borderRadius: "4px",
                  border: "1px solid #a5d6a7"
                }}>
                  {invoice.driverName && <span><strong>👨‍💼 Driver:</strong> {invoice.driverName}</span>}
                  {invoice.driverName && invoice.vehicleNumber && <span style={{ marginLeft: "20px" }}>|</span>}
                  {invoice.vehicleNumber && <span style={{ marginLeft: invoice.driverName ? "20px" : "0" }}><strong>🚛 Vehicle:</strong> {invoice.vehicleNumber}</span>}
                </div>
              )}
              
              <p className="grand-total"><strong>Amount Receivable:</strong> {invoice.grandTotal ?? "0"}</p>
            </div>
          </div>
        );
      })}
      
      <div style={{ position: "absolute", top: "-9999px" }}>
        <UniversalInvoicePrint ref={printRef} invoice={printInvoice} type="sale" />
      </div>

      {fullScreenImage && (
        <div onClick={() => setFullScreenImage(null)} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.8)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999, cursor: "zoom-out" }}>
          <img src={fullScreenImage} alt="Full Screen Invoice" style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain", boxShadow: "0 0 20px #fff" }} />
        </div>
      )}
    </div>
  );
}

export default ViewSales;